/**
 * OfficialLinks Component
 *
 * Quick access links to official emergency service websites and resources.
 * These should be prominently displayed so users can easily access
 * authoritative information.
 */

interface OfficialLink {
  name: string;
  url: string;
  description: string;
  icon: string;
  isEmergency?: boolean;
}

const OFFICIAL_LINKS: OfficialLink[] = [
  {
    name: 'Triple Zero (000)',
    url: 'tel:000',
    description: 'Emergency calls only',
    icon: '📞',
    isEmergency: true,
  },
  {
    name: 'NSW RFS',
    url: 'https://www.rfs.nsw.gov.au/fire-information/fires-near-me',
    description: 'Official fire information',
    icon: '🔥',
  },
  {
    name: 'Hazards Near Me',
    url: 'https://hazardsnear.me/',
    description: 'NSW emergency app & web',
    icon: '📱',
  },
  {
    name: 'ABC Emergency',
    url: 'https://www.abc.net.au/emergency',
    description: 'Emergency broadcasts',
    icon: '📻',
  },
  {
    name: 'Bureau of Meteorology',
    url: 'http://www.bom.gov.au/nsw/warnings/',
    description: 'Weather warnings',
    icon: '🌤️',
  },
];

interface OfficialLinksProps {
  isExpanded?: boolean;
  onToggle?: () => void;
}

export default function OfficialLinks({
  isExpanded = false,
  onToggle,
}: OfficialLinksProps) {
  return (
    <div className="official-links">
      <button
        className="official-links-header"
        onClick={onToggle}
        aria-expanded={isExpanded}
      >
        <span className="official-links-title">
          <span className="official-links-icon">🔗</span>
          Official Resources
        </span>
        <span className="official-links-toggle">{isExpanded ? '−' : '+'}</span>
      </button>

      {isExpanded && (
        <div className="official-links-content">
          <p className="official-links-intro">
            For official information and emergency updates, use these official sources:
          </p>

          <div className="official-links-list">
            {OFFICIAL_LINKS.map((link) => (
              <a
                key={link.name}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`official-link ${link.isEmergency ? 'official-link-emergency' : ''}`}
              >
                <span className="official-link-icon">{link.icon}</span>
                <div className="official-link-content">
                  <span className="official-link-name">{link.name}</span>
                  <span className="official-link-description">{link.description}</span>
                </div>
                <span className="official-link-arrow">→</span>
              </a>
            ))}
          </div>

          <div className="official-links-footer">
            <p>
              <strong>Bush Fire Information Line:</strong>{' '}
              <a href="tel:1800679737">1800 NSW RFS (1800 679 737)</a>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
